<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import { useI18n } from "vue-i18n";

definePage({
  meta: {
    layout: "public",
    requiresAuth: false,
  },
});
const { t } = useI18n();
</script>
<template>
  <div class="tw:!relative">
    <div class="tw:fixed tw:top-4 tw:right-4 tw:md:top-16 tw:md:right-16 tw:z-layer-100">
      <v-btn
        icon
        rounded="lg"
        color="secondary"
        to="/"
        aria-label="Close"
      >
        <v-icon icon="mdi-close" />
      </v-btn>
    </div>
    <div class="tw:!p-4 tw:md:!p-8">
      <div>
        <div>
          <h1 class="tw:text-2xl tw:md:text-[3.175rem] tw:!tracking-tight tw:!mt-8 tw:font-bold">
            {{ t("features.privacyPolicy.title") }}
          </h1>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.effectiveDate") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.intro") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.youMeans") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.readCarefully") }}
          </p>
          <ol class="tw:!mb-4 tw:list-decimal tw:!pl-8 tw:text-justify">
            <li class="tw:font-semibold tw:text-[1.15rem] tw:tracking-tight">
              {{ t("features.privacyPolicy.generalDisclosures.title") }}
            </li>
            <li class="tw:font-semibold tw:text-[1.15rem] tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.title") }}
            </li>
            <li class="tw:font-semibold tw:text-[1.15rem] tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.title") }}
            </li>
            <li class="tw:font-semibold tw:text-[1.15rem] tw:tracking-tight">
              {{ t("features.privacyPolicy.childrenInfo.title") }}
            </li>
            <li class="tw:font-semibold tw:text-[1.15rem] tw:tracking-tight">
              {{ t("features.privacyPolicy.marketing.title") }}
            </li>
            <li class="tw:font-semibold tw:text-[1.15rem] tw:tracking-tight">
              {{ t("features.privacyPolicy.integrations.title") }}
            </li>
            <li class="tw:font-semibold tw:text-[1.15rem] tw:tracking-tight">
              {{ t("features.privacyPolicy.sections.security") }}
            </li>
            <li class="tw:font-semibold tw:text-[1.15rem] tw:tracking-tight">
              {{ t("features.privacyPolicy.caRights.title") }}
            </li>
            <li class="tw:font-semibold tw:text-[1.15rem] tw:tracking-tight">
              {{ t("features.privacyPolicy.changes.title") }}
            </li>
            <li class="tw:font-semibold tw:text-[1.15rem] tw:tracking-tight">
              {{ t("features.privacyPolicy.contact.title") }}
            </li>
          </ol>

          <!-- GENERAL DISCLOSURES -->
          <h2
            class="tw:text-xl tw:md:text-[2.125rem] tw:tracking-tight tw:font-bold tw:!mt-10 tw:md:!mt-16"
          >
            {{ t("features.privacyPolicy.generalDisclosures.title") }}
          </h2>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.generalDisclosures.hosting") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.generalDisclosures.healthLaws") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.generalDisclosures.onlyThisPolicy") }}
          </p>

          <!-- WHAT INFORMATION WE COLLECT AND DISCLOSE AND FOR WHAT PURPOSES -->
          <h2
            class="tw:text-xl tw:md:text-[2.125rem] tw:tracking-tight tw:font-bold tw:!mt-10 tw:md:!mt-16"
          >
            {{ t("features.privacyPolicy.infoCollection.title") }}
          </h2>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.context") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.visitOrUseIntro") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.visitOrUse.list.onlineIdentifier") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.visitOrUse.list.geolocation") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.visitOrUse.list.internetActivity") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.visitOrUse.list.deviceInfo") }}
            </li>
          </ul>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.whyWeCollectThis") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.whyWeCollectThisList.accessSite") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.whyWeCollectThisList.secureNetworks") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.whyWeCollectThisList.siteAnalytics") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.whyWeCollectThisList.developEnhance") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.whyWeCollectThisList.advertising") }}
            </li>
          </ul>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.discloseTo") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.discloseToList.serviceProviders") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.discloseToList.pluginEntities") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.discloseToList.adPartners") }}
            </li>
          </ul>
          <!-- If you enter information into our Site... -->
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.enterInfoIntro") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.enterInfo.list.identifier") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.engageServices.list.contact") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.enterInfo.list.location") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.enterInfo.list.payment") }}
            </li>
          </ul>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.whyWeCollectThis") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.enterInfo.whyList.connectProviders") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t("features.privacyPolicy.infoCollection.engageServices.whyList.provideServices")
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.engageServices.whyList.developEnhance") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.engageServices.whyList.processTransactions"
                )
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.engageServices.whyList.contactAboutServices"
                )
              }}
            </li>
          </ul>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.discloseTo") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t("features.privacyPolicy.infoCollection.enterInfo.discloseToList.serviceProviders")
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.engageServices.discloseToList.connectedProviders"
                )
              }}
            </li>
          </ul>
          <!-- If you engage with our Services... -->
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.engageServicesIntro") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.engageServices.list.identifier") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.engageServices.list.contact") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.engageServices.list.payment") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.engageServices.list.insurance") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.engageServices.list.voice") }}
            </li>
          </ul>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.whyWeCollectThis") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t("features.privacyPolicy.infoCollection.engageServices.whyList.provideServices")
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t("features.privacyPolicy.infoCollection.engageServices.whyList.connectProviders")
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.engageServices.whyList.improveAI") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.engageServices.whyList.developEnhance") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.engageServices.whyList.processTransactions"
                )
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.engageServices.whyList.contactAboutServices"
                )
              }}
            </li>
          </ul>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.discloseTo") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.engageServices.discloseToList.serviceProviders"
                )
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.engageServices.discloseToList.connectedProviders"
                )
              }}
            </li>
          </ul>
          <!-- Smile Simulation App -->
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.smileAppIntro") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.smileApp.list.login") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.enterInfo.list.identifier") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.smileApp.list.contact") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.enterInfo.list.location") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.smileApp.list.visual") }}
            </li>
          </ul>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.smileApp.why.login") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.smileApp.why.identifierContactLocation") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.smileApp.why.identifierContactLocationList.connectDental"
                )
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t("features.privacyPolicy.infoCollection.engageServices.whyList.provideServices")
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.infoCollection.engageServices.whyList.developEnhance") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.smileApp.why.identifierContactLocationList.market"
                )
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.engageServices.whyList.processTransactions"
                )
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.smileApp.why.identifierContactLocationList.contactAboutServices"
                )
              }}
            </li>
          </ul>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.smileApp.why.visual") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.smileApp.disclose.login") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{
              t("features.privacyPolicy.infoCollection.smileApp.disclose.identifierContactLocation")
            }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.smileApp.disclose.identifierContactLocationList.serviceProviders"
                )
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.smileApp.disclose.identifierContactLocationList.applicationProvider"
                )
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.smileApp.disclose.identifierContactLocationList.marketingPartners"
                )
              }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{
                t(
                  "features.privacyPolicy.infoCollection.smileApp.disclose.identifierContactLocationList.dentalProviders"
                )
              }}
            </li>
          </ul>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.smileApp.visualCollectedByThirdParty") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.furtherDisclosuresIntro") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            <strong>{{ t("features.privacyPolicy.infoCollection.auditors") }}</strong>
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            <strong>{{ t("features.privacyPolicy.infoCollection.authorizedEntities") }}</strong>
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            <strong>{{ t("features.privacyPolicy.infoCollection.affiliates") }}</strong>
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            <strong>{{ t("features.privacyPolicy.infoCollection.corporateTransactions") }}</strong>
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            <strong>{{ t("features.privacyPolicy.infoCollection.legalPurposes") }}</strong>
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.infoCollection.aggregatedInfo") }}
          </p>
          <!-- COOKIES AND ONLINE TECHNOLOGIES POLICY -->
          <h2
            class="tw:text-xl tw:md:text-[2.125rem] tw:tracking-tight tw:font-bold tw:!mt-10 tw:md:!mt-16"
          >
            {{ t("features.privacyPolicy.cookiesPolicy.title") }}
          </h2>
          <h3 class="tw:font-bold tw:text-2xl tw:tracking-tight tw:!mt-6">
            {{ t("features.privacyPolicy.cookiesPolicy.whatIsCookie") }}
          </h3>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.whatIsCookieDesc") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.types.session") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.types.persistent") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.types.firstParty") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.types.thirdParty") }}
            </li>
          </ul>
          <h3 class="tw:font-bold tw:text-2xl tw:tracking-tight tw:!mt-6">
            {{ t("features.privacyPolicy.cookiesPolicy.ourCollection") }}
          </h3>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.ourCollectionDesc") }}
          </p>
          <div class="tw:!p-4 tw:overflow-x-auto">
            <table
              class="tw:w-full tw:min-w-[480px] tw:border tw:border-[#d6e1ea] tw:border-collapse"
            >
              <thead>
                <tr>
                  <th
                    class="tw:text-left tw:!p-4 tw:text-xl tw:font-bold tw:text-[#244169] tw:border-b tw:border-[#d6e1ea]"
                  >
                    {{ t("generic.fields.category") }}
                  </th>
                  <th
                    class="tw:text-left tw:!p-4 tw:text-xl tw:font-bold tw:text-[#244169] tw:border-b tw:border-[#d6e1ea]"
                  >
                    {{ t("generic.fields.description") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    class="tw:align-top tw:!p-4 tw:font-bold tw:text-[#244169] tw:border-b tw:border-[#d6e1ea]"
                  >
                    {{ t("features.privacyPolicy.cookiesPolicy.table.essential") }}
                  </td>
                  <td
                    class="tw:align-top tw:!p-4 tw:font-normal tw:text-[#244169] tw:border-b tw:border-[#d6e1ea]"
                  >
                    {{ t("features.privacyPolicy.cookiesPolicy.table.essentialDesc") }}
                  </td>
                </tr>
                <tr>
                  <td
                    class="tw:align-top tw:!p-4 tw:font-bold tw:text-[#244169] tw:border-b tw:border-[#d6e1ea]"
                  >
                    {{ t("features.privacyPolicy.cookiesPolicy.table.analytics") }}
                  </td>
                  <td
                    class="tw:align-top tw:!p-4 tw:font-normal tw:text-[#244169] tw:border-b tw:border-[#d6e1ea]"
                  >
                    {{ t("features.privacyPolicy.cookiesPolicy.table.analyticsDesc") }}
                  </td>
                </tr>
                <tr>
                  <td
                    class="tw:align-top tw:!p-4 tw:font-bold tw:text-[#244169] tw:border-b tw:border-[#d6e1ea]"
                  >
                    {{ t("features.privacyPolicy.cookiesPolicy.table.personalization") }}
                  </td>
                  <td
                    class="tw:align-top tw:!p-4 tw:font-normal tw:text-[#244169] tw:border-b tw:border-[#d6e1ea]"
                  >
                    {{ t("features.privacyPolicy.cookiesPolicy.table.personalizationDesc") }}
                  </td>
                </tr>
                <tr>
                  <td class="tw:align-top tw:!p-4 tw:font-bold tw:text-[#244169]">
                    {{ t("features.privacyPolicy.cookiesPolicy.table.targeting") }}
                  </td>
                  <td class="tw:align-top tw:!p-4 tw:font-normal tw:text-[#244169]">
                    {{ t("features.privacyPolicy.cookiesPolicy.table.targetingDesc") }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <h3 class="tw:font-bold tw:text-2xl tw:tracking-tight tw:!mt-6">
            {{ t("features.privacyPolicy.cookiesPolicy.thirdPartyCookies.title") }}
          </h3>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.thirdPartyCookies.desc") }}
          </p>
          <h3 class="tw:font-bold tw:text-2xl tw:tracking-tight tw:!mt-6">
            {{ t("generic.navbar.paths.analytics") }}
          </h3>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.analytics.desc1") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.analytics.desc2") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.analytics.googleAnalytics") }}
          </p>
          <h3 class="tw:font-bold tw:text-2xl tw:tracking-tight tw:!mt-6">
            {{ t("features.privacyPolicy.cookiesPolicy.advertising.title") }}
          </h3>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.advertising.desc1") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.advertising.desc2") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.advertising.desc3") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.advertising.desc4") }}
          </p>
          <h3 class="tw:font-bold tw:text-2xl tw:tracking-tight tw:!mt-6">
            {{ t("features.privacyPolicy.cookiesPolicy.accessibilityWidget.title") }}
          </h3>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.accessibilityWidget.desc") }}
          </p>
          <h3 class="tw:font-bold tw:text-2xl tw:tracking-tight tw:!mt-6">
            {{ t("features.privacyPolicy.cookiesPolicy.dnt.title") }}
          </h3>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.dnt.desc") }}
          </p>
          <h3 class="tw:font-bold tw:text-2xl tw:tracking-tight tw:!mt-6">
            {{ t("features.privacyPolicy.cookiesPolicy.webBeacons.title") }}
          </h3>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.webBeacons.desc1") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.webBeacons.desc2") }}
          </p>
          <h3 class="tw:font-bold tw:text-2xl tw:tracking-tight tw:!mt-6">
            {{ t("features.privacyPolicy.cookiesPolicy.socialMediaPlugins.title") }}
          </h3>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.socialMediaPlugins.desc") }}
          </p>
          <h3 class="tw:font-bold tw:text-2xl tw:tracking-tight tw:!mt-6">
            {{ t("features.privacyPolicy.cookiesPolicy.optOut.title") }}
          </h3>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.optOut.desc") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.optOut.list.cookiePrefs") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.optOut.list.nai")
              }}<a
                class="tw:text-blue-600"
                href="http://www.networkadvertising.org/choices"
                >http://www.networkadvertising.org/choices</a
              >
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.optOut.list.daa")
              }}<a
                class="tw:text-blue-600"
                href="http://www.aboutads.info/"
                >http://www.aboutads.info/</a
              >
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.optOut.list.googlePlugin")
              }}<a
                class="tw:text-blue-600"
                href="http://tools.google.com/dlpage/gaoptout"
                >http://tools.google.com/dlpage/gaoptout</a
              >
            </li>
          </ul>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.browserControl") }}
          </p>
          <ul class="tw:!pl-8 list-disc tw:!mb-4 tw:text-justify">
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.browserControlList.chrome") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.browserControlList.firefox") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.browserControlList.safari") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.browserControlList.opera") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.browserControlList.ie") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.browserControlList.edge") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.browserControlList.safariIos") }}
            </li>
            <li class="tw:font-semibold tw:tracking-tight">
              {{ t("features.privacyPolicy.cookiesPolicy.browserControlList.chromeAndroid") }}
            </li>
          </ul>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.restrictingWarning") }}
            <a
              class="tw:text-blue-600"
              href="http://www.allaboutcookies.org"
              target="_blank"
              >www.allaboutcookies.org</a
            >
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.cookiesPolicy.ftcFirst") }}
            <a
              class="tw:text-blue-600"
              href="http://www.consumer.ftc.gov"
              target="_blank"
              >www.consumer.ftc.gov</a
            >
            {{ t("features.privacyPolicy.cookiesPolicy.ftcSecond") }}
          </p>
          <h2
            class="tw:text-xl tw:md:text-[2.125rem] tw:tracking-tight tw:font-bold tw:!mt-10 tw:md:!mt-16"
          >
            {{ t("features.privacyPolicy.childrenInfo.title") }}
          </h2>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.childrenInfo.desc") }}
          </p>
          <h2
            class="tw:text-xl tw:md:text-[2.125rem] tw:tracking-tight tw:font-bold tw:!mt-10 tw:md:!mt-16"
          >
            {{ t("features.privacyPolicy.marketing.title") }}
          </h2>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.marketing.desc") }}
          </p>
          <h2
            class="tw:text-xl tw:md:text-[2.125rem] tw:tracking-tight tw:font-bold tw:!mt-10 tw:md:!mt-16"
          >
            {{ t("features.privacyPolicy.integrations.title") }}
          </h2>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.integrations.desc") }}
          </p>
          <h2
            class="tw:text-xl tw:md:text-[2.125rem] tw:tracking-tight tw:font-bold tw:!mt-10 tw:md:!mt-16"
          >
            {{ t("features.privacyPolicy.sections.security") }}
          </h2>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.security.desc") }}
          </p>
          <h2
            class="tw:text-xl tw:md:text-[2.125rem] tw:tracking-tight tw:font-bold tw:!mt-10 tw:md:!mt-16"
          >
            {{ t("features.privacyPolicy.caRights.title") }}
          </h2>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.caRights.desc1") }}
          </p>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.caRights.desc2") }}
          </p>
          <h2
            class="tw:text-xl tw:md:text-[2.125rem] tw:tracking-tight tw:font-bold tw:!mt-10 tw:md:!mt-16"
          >
            {{ t("features.privacyPolicy.changes.title") }}
          </h2>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.changes.desc") }}
          </p>
          <h2
            class="tw:text-xl tw:md:text-[2.125rem] tw:tracking-tight tw:font-bold tw:!mt-10 tw:md:!mt-16"
          >
            {{ t("features.privacyPolicy.contact.title") }}
          </h2>
          <p class="tw:!mb-4 tw:font-medium tw:text-justify">
            {{ t("features.privacyPolicy.contact.desc")
            }}<a
              class="tw:text-blue-600"
              href="mailto:privacy@cotreatme.com"
            >
              privacy@cotreatme.com.</a
            >
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
